import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { AppState, AppDispatch } from '@store/index';
import { logout } from '@store/reducers/authReducer';
import { USER_INFO } from '@constants/lsconstants';

const API_URL = process.env.REACT_APP_API_URL;
/** Poll while tab is visible; 120s cuts sustained load ~4x vs 30s. */
const POLL_INTERVAL_MS = 120_000;

/**
 * Logs out the SSO session by injecting a hidden iframe pointing to the SSO
 * logout URL, then polling until the SSO session is gone (up to 5 attempts).
 * Resolves when the SSO logout is confirmed or after the last attempt.
 */
export async function performSsoLogout(): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.src = process.env.SSO_URL + '/logout';
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    // Remove iframe after 1 s regardless
    setTimeout(() => {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    }, 1000);

    let attempts = 0;
    const check = async () => {
      attempts++;
      try {
        const res = await fetch(process.env.SSO_URL + '/api/user/v1/me', {
          credentials: 'include',
        });
        if (res.status === 401 || res.status === 403) {
          resolve();
        } else if (attempts < 5) {
          setTimeout(check, 1000);
        } else {
          resolve(); // give up waiting, proceed anyway
        }
      } catch {
        // Network error / CORS means the SSO session is gone
        resolve();
      }
    };

    // Start first check after 2 s (give the iframe time to fire)
    setTimeout(check, 2000);
  });
}

/**
 * Polls the backend while the tab is visible to verify the current session_key
 * is still the active one. Pauses in background tabs (Page Visibility API).
 * If another device has taken over the session, logs out the SSO session first,
 * then clears local auth state and redirects to login.
 */
export function useSessionWatcher() {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector((state: AppState) => state.auth.access_token);
  const sessionKey = useSelector((state: AppState) => state.auth.session_key);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Only poll when the user is authenticated and has a session key
    if (!accessToken || !sessionKey) return;

    // Skip validation during OAuth callback to avoid race with new login flow
    if (window.location.pathname === '/callback') return;

    let cancelled = false;

    const stopInterval = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const check = async () => {
      if (cancelled) return;
      try {
        const response = await fetch(`${API_URL}/api-validate-session/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ session_key: sessionKey }),
        });

        if (cancelled) return;

        if (!response.ok) {
          // 401 means the JWT itself is expired — handled elsewhere
          return;
        }

        const data = await response.json();
        if (cancelled) return;

        if (!data.valid) {
          // Stop polling immediately so we don't fire again mid-logout
          stopInterval();

          toast.warning(
            'Tài khoản của bạn đã đăng nhập từ thiết bị khác. Bạn đã bị đăng xuất.',
            { autoClose: 5000 },
          );

          // Log out SSO session first, then clear local state
          await performSsoLogout();

          if (cancelled) return;

          sessionStorage.clear();
          localStorage.removeItem(USER_INFO);
          dispatch(logout());
          window.location.replace('/user/login');
        }
      } catch {
        // Network error — silently ignore, will retry next interval
      }
    };

    const startInterval = () => {
      stopInterval();
      timerRef.current = setInterval(check, POLL_INTERVAL_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
        startInterval();
      } else {
        stopInterval();
      }
    };

    // Start only when tab is visible; background tabs stay quiet until focused
    if (document.visibilityState === 'visible') {
      check();
      startInterval();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [accessToken, sessionKey, dispatch]);
}
